// Dynamic AI Explanations — Claude-powered question explanations via Firebase Cloud Function
// Multi-cert: cache paths are cert-scoped via certPath()
// Caches results in Firebase RTDB to avoid redundant API calls.

import { certPath, read, write } from './firebase-store.js';

// Cache TTL: 30 days (explanations don't go stale often)
const CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

// ── Explanation Types ──
// 'question'       — explain why the correct answer is right
// 'misconception'  — explain a detected misconception pattern
// 'concept'        — explain a concept the user is weak on
// 'readiness'      — summarize readiness status with study advice

/**
 * Get an AI-generated explanation for a question (cert-scoped cache).
 *
 * @param {string} userId
 * @param {string} certId
 * @param {string} questionId
 * @param {object} context
 * @returns {Promise<{explanation, cached, type}>}
 */
export async function getQuestionExplanation(userId, certId, questionId, context) {
  const cacheKey = certPath(userId, certId, 'explanations/' + questionId);
  try {
    const cached = await read(cacheKey);
    if (cached && cached.explanation && !_isExpired(cached.ts)) {
      return { explanation: cached.explanation, cached: true, type: 'question' };
    }
  } catch (e) { /* cache miss */ }

  const prompt = _buildQuestionPrompt(certId, context);

  const result = await _callCloudFunction('generateExplanation', {
    type: 'question',
    questionId,
    prompt,
    userId,
    certId
  });

  if (result && result.explanation) {
    try {
      await write(cacheKey, {
        explanation: result.explanation,
        ts: new Date().toISOString(),
        model: result.model || 'unknown'
      });
    } catch (e) { /* cache write failed — non-fatal */ }

    return { explanation: result.explanation, cached: false, type: 'question' };
  }

  return _fallbackExplanation('question', context);
}

/**
 * Get an AI explanation for a detected misconception (cert-scoped cache).
 *
 * @param {string} userId
 * @param {string} certId
 * @param {object} misconception
 * @returns {Promise<{explanation, cached, type}>}
 */
export async function getMisconceptionExplanation(userId, certId, misconception) {
  const cacheKey = certPath(userId, certId, 'explanations/misconception_' + misconception.name);
  try {
    const cached = await read(cacheKey);
    if (cached && cached.explanation && !_isExpired(cached.ts)) {
      return { explanation: cached.explanation, cached: true, type: 'misconception' };
    }
  } catch (e) { /* cache miss */ }

  const prompt = _buildMisconceptionPrompt(certId, misconception);

  const result = await _callCloudFunction('generateExplanation', {
    type: 'misconception',
    misconceptionName: misconception.name,
    prompt,
    userId,
    certId
  });

  if (result && result.explanation) {
    try {
      await write(cacheKey, {
        explanation: result.explanation,
        ts: new Date().toISOString(),
        model: result.model || 'unknown'
      });
    } catch (e) { /* non-fatal */ }

    return { explanation: result.explanation, cached: false, type: 'misconception' };
  }

  return _fallbackExplanation('misconception', misconception);
}

/**
 * Get a readiness insight (cert-scoped cache).
 *
 * @param {string} userId
 * @param {string} certId
 * @param {object} profile
 * @returns {Promise<{explanation, cached, type}>}
 */
export async function getReadinessInsight(userId, certId, profile) {
  const cacheKey = certPath(userId, certId, 'explanations/readiness');
  try {
    const cached = await read(cacheKey);
    // Short TTL: 6 hours for readiness insights
    if (cached && cached.explanation && !_isExpired(cached.ts, 6 * 60 * 60 * 1000)) {
      return { explanation: cached.explanation, cached: true, type: 'readiness' };
    }
  } catch (e) { /* cache miss */ }

  const prompt = _buildReadinessPrompt(certId, profile);

  const result = await _callCloudFunction('generateExplanation', {
    type: 'readiness',
    prompt,
    userId,
    certId
  });

  if (result && result.explanation) {
    try {
      await write(cacheKey, {
        explanation: result.explanation,
        ts: new Date().toISOString(),
        model: result.model || 'unknown'
      });
    } catch (e) { /* non-fatal */ }

    return { explanation: result.explanation, cached: false, type: 'readiness' };
  }

  return _fallbackExplanation('readiness', profile);
}

/**
 * General-purpose engine insight (cert-scoped).
 *
 * @param {string} userId
 * @param {string} certId
 * @param {string} type
 * @param {object} context
 * @returns {Promise<{type, explanation, cached, data}>}
 */
export async function getInsight(userId, certId, type, context) {
  switch (type) {
    case 'question':
      return getQuestionExplanation(userId, certId, context.questionId, context);
    case 'misconception':
      return getMisconceptionExplanation(userId, certId, context);
    case 'readiness':
      return getReadinessInsight(userId, certId, context);
    default:
      return { type, explanation: null, cached: false, data: null };
  }
}

// ── Cloud Function Caller ──

async function _callCloudFunction(fnName, data) {
  try {
    if (typeof firebase !== 'undefined' && firebase.functions) {
      const fn = firebase.functions().httpsCallable(fnName);
      const result = await fn(data);
      return result.data;
    }

    const projectId = 'life-hub-9bc8b';
    const url = 'https://us-central1-' + projectId + '.cloudfunctions.net/' + fnName;

    const headers = { 'Content-Type': 'application/json' };
    if (typeof firebase !== 'undefined' && firebase.auth) {
      const user = firebase.auth().currentUser;
      if (user) {
        const token = await user.getIdToken();
        headers['Authorization'] = 'Bearer ' + token;
      }
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ data })
    });
    if (response.ok) {
      const json = await response.json();
      return json.result || json;
    }
  } catch (e) {
    console.warn('[Explanations] Cloud Function call failed:', e.message);
  }

  return null;
}

// ── Prompt Sanitization ──

function _sanitize(text, maxLen = 2000) {
  if (!text || typeof text !== 'string') return '';
  let clean = text.replace(/<[^>]*>/g, '');
  clean = clean.replace(/\s+/g, ' ').trim();
  if (clean.length > maxLen) clean = clean.slice(0, maxLen) + '...';
  return clean;
}

// ── Cert Name Resolution ──
const CERT_DISPLAY_NAMES = {
  md102: 'MD-102',
  sc900: 'SC-900',
  az900: 'AZ-900',
  az104: 'AZ-104',
  ai900: 'AI-900',
  aplus1: 'CompTIA A+ Core 1',
  aplus2: 'CompTIA A+ Core 2',
  netplus: 'CompTIA Network+',
  secplus: 'CompTIA Security+'
};

// Legacy: kept for backward compat but now certId is passed to prompt builders
let _activeCertName = 'MD-102';

export function setExplanationCert(certId) {
  _activeCertName = CERT_DISPLAY_NAMES[certId] || certId.toUpperCase();
}

function _getCertDisplayName(certId) {
  if (certId) return CERT_DISPLAY_NAMES[certId] || certId.toUpperCase();
  return _activeCertName;
}

// ── Prompt Builders ──

function _buildQuestionPrompt(certId, ctx) {
  const certName = _getCertDisplayName(certId);
  const parts = [];
  parts.push('You are an expert tutor for the ' + certName + ' certification exam.');
  parts.push('Explain why the correct answer is right and why the selected wrong answer is incorrect.');
  parts.push('Be concise (2-3 sentences). Use simple language a student would understand.');
  parts.push('');

  if (ctx.question) {
    if (ctx.question.stem || ctx.question.question) {
      parts.push('Question: ' + _sanitize(ctx.question.stem || ctx.question.question, 500));
    }
  }
  if (ctx.correctChoice) parts.push('Correct answer: ' + _sanitize(ctx.correctChoice, 200));
  if (ctx.selectedChoice && !ctx.correct) parts.push('Student chose: ' + _sanitize(ctx.selectedChoice, 200));
  if (ctx.skills && ctx.skills.length > 0) parts.push('Skills tested: ' + ctx.skills.map(s => _sanitize(s, 50)).join(', '));
  if (ctx.conceptTags && ctx.conceptTags.length > 0) parts.push('Concepts: ' + ctx.conceptTags.map(c => _sanitize(c, 50)).join(', '));

  return parts.join('\n');
}

function _buildMisconceptionPrompt(certId, misconception) {
  const certName = _getCertDisplayName(certId);
  const parts = [];
  parts.push('You are an expert tutor for the ' + certName + ' certification exam.');
  parts.push('A student has a recurring misconception. Explain the correct understanding clearly.');
  parts.push('Be encouraging. 2-3 sentences max.');
  parts.push('');
  parts.push('Misconception area: ' + _sanitize(misconception.name, 200));
  parts.push('Error count: ' + misconception.errorCount);
  parts.push('Severity: ' + misconception.severity);
  if (misconception.relatedConcepts && misconception.relatedConcepts.length > 0) {
    parts.push('Related concepts: ' + misconception.relatedConcepts.join(', '));
  }

  return parts.join('\n');
}

function _buildReadinessPrompt(certId, profile) {
  const certName = _getCertDisplayName(certId);
  const parts = [];
  parts.push('You are an expert tutor for the ' + certName + ' certification exam.');
  parts.push('Give the student a brief, encouraging study recommendation based on their profile.');
  parts.push('2-3 sentences. Be specific about what to focus on next.');
  parts.push('');

  if (profile.predictedPassRate != null) {
    parts.push('Predicted pass rate: ' + Math.round(profile.predictedPassRate * 100) + '%');
  }
  if (profile.domainMastery) {
    for (const [key, val] of Object.entries(profile.domainMastery)) {
      parts.push('Domain ' + key + ' mastery: ' + Math.round(val * 100) + '%');
    }
  }
  if (profile.cardsDueToday) parts.push('Cards due today: ' + profile.cardsDueToday);
  if (profile.weakConcepts && profile.weakConcepts.length > 0) {
    const weakNames = profile.weakConcepts.slice(0, 3).map(w => w.concept);
    parts.push('Weak areas: ' + weakNames.join(', '));
  }

  return parts.join('\n');
}

// ── Fallback Explanations ──

function _fallbackExplanation(type, context) {
  const fallbacks = {
    'question': {
      type: 'question',
      explanation: 'Review the correct answer and compare it with your selection. Focus on the key differences in how each option applies to the scenario.',
      cached: false
    },
    'misconception': {
      type: 'misconception',
      explanation: 'You\'ve made this mistake a few times. Take a moment to review the related concept before continuing. Practice questions in this area will help reinforce the correct understanding.',
      cached: false
    },
    'readiness': {
      type: 'readiness',
      explanation: 'Keep up your study routine. Focus on your weakest domains and review any due flashcards to maintain retention.',
      cached: false
    }
  };

  return fallbacks[type] || { type, explanation: null, cached: false };
}

// ── Helpers ──

function _isExpired(ts, ttl) {
  if (!ts) return true;
  const age = Date.now() - new Date(ts).getTime();
  return age > (ttl || CACHE_TTL_MS);
}
